import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import ws from '../services/wsService';
import LiveIndicator from '../components/LiveIndicator';
import { useAuthStore } from '../stores/authStore';
import { ticketHref } from '../utils/ticketView';
import {
  PlusIcon, TicketIcon, ChartBarIcon, InboxStackIcon, ClockIcon, PauseCircleIcon,
} from '@heroicons/react/24/outline';

type Period = '7d' | '30d' | '90d';
const PERIODS: { key: Period; label: string; days: number }[] = [
  { key: '7d', label: '7 дней', days: 7 },
  { key: '30d', label: '30 дней', days: 30 },
  { key: '90d', label: '90 дней', days: 90 },
];

const fmtMin = (m: number) => {
  if (!m || m <= 0) return '—';
  if (m < 60) return `${Math.round(m)} мин`;
  const h = Math.floor(m / 60); const r = Math.round(m % 60);
  return r ? `${h} ч ${r} м` : `${h} ч`;
};
const ago = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'только что';
  if (s < 3600) return `${Math.floor(s / 60)} мин`;
  if (s < 86400) return `${Math.floor(s / 3600)} ч`;
  return `${Math.floor(s / 86400)} д`;
};
const ddmm = (iso: string) => { const [, m, d] = iso.split('-'); return `${d}.${m}`; };
const clubName = (t: any) =>
  t.telegramChatTitle || (t.telegramUsername ? `@${t.telegramUsername}` : `${t.telegramFirstName || ''} ${t.telegramLastName || ''}`.trim()) || 'клуб';

const Tile: React.FC<{ label: string; value: React.ReactNode; icon: React.FC<any>; tone?: 'amber' | 'blue' | 'gray' | 'violet'; to?: string; hint?: string }> =
  ({ label, value, icon: Icon, tone = 'gray', to, hint }) => {
    const tones: Record<string, string> = {
      amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
      gray: 'bg-muted text-muted-foreground',
    };
    const body = (
      <div className="flex items-center gap-4 rounded-xl border border-border bg-card shadow-sm p-4 h-full hover:border-primary/50 transition-colors">
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <div className="text-3xl font-semibold tabular-nums leading-none text-foreground">{value}</div>
          <div className="text-sm text-muted-foreground mt-1 truncate">{label}</div>
          {hint && <div className="text-xs text-muted-foreground/80 truncate">{hint}</div>}
        </div>
      </div>
    );
    return to ? <Link to={to} className="block">{body}</Link> : body;
  };

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <div className="min-w-0">
    <div className="text-sm text-muted-foreground truncate">{label}</div>
    <div className="text-2xl font-semibold tabular-nums text-foreground leading-tight mt-0.5">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</div>}
  </div>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [period, setPeriod] = useState<Period>('7d');
  const [overview, setOverview] = useState<any>(null);
  const [sla, setSla] = useState<any>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [now, setNow] = useState<{ newT: number; inProgress: number; paused: number; newList: any[] }>({ newT: 0, inProgress: 0, paused: 0, newList: [] });
  const [loading, setLoading] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [hoverDay, setHoverDay] = useState<number | null>(null);
  const [pulse, setPulse] = useState(false);
  const periodRef = useRef(period);
  useEffect(() => { periodRef.current = period; }, [period]);

  const fetchAll = useCallback(async (silent = false) => {
    const p = periodRef.current;
    try {
      const [ov, sl, tl, ops, nw, ip, pa] = await Promise.all([
        api.get('/api/tickets/stats/overview', { params: { period: p } }),
        api.get('/api/stats/sla', { params: { period: p } }).catch(() => null),
        api.get('/api/stats/timeline', { params: { period: p, groupBy: 'day' } }).catch(() => null),
        api.get('/api/stats/operators', { params: { period: p } }).catch(() => null),
        api.get('/api/tickets/', { params: { status: 'new', limit: 8, sortBy: 'createdAt', sortOrder: 'asc' } }),
        api.get('/api/tickets/', { params: { status: 'in_progress', limit: 1 } }).catch(() => null),
        api.get('/api/tickets/', { params: { status: 'paused', limit: 1 } }).catch(() => null),
      ]);
      setOverview(ov.data?.data?.overview || null);
      setSla(sl?.data?.data || null);
      setTimeline(tl?.data?.data?.timelineStats || []);
      setOperators(ops?.data?.data?.operatorStats || []);
      setNow({
        newT: nw.data?.data?.pagination?.total || 0,
        newList: nw.data?.data?.tickets || [],
        inProgress: ip?.data?.data?.pagination?.total || 0,
        paused: pa?.data?.data?.pagination?.total || 0,
      });
      setLastSyncedAt(new Date());
      if (silent) { setPulse(true); window.setTimeout(() => setPulse(false), 1000); }
    } catch (e) {
      if (!silent) console.error('dashboard load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [period, fetchAll]);

  useEffect(() => {
    ws.joinDashboard();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; fetchAll(true); }, 1500);
    };
    ws.on('new_ticket', schedule); ws.on('ticket_updated', schedule); ws.on('new_message', schedule);
    return () => {
      ws.off('new_ticket', schedule); ws.off('ticket_updated', schedule); ws.off('new_message', schedule);
      if (timer) clearTimeout(timer);
    };
  }, [fetchAll]);

  const days = PERIODS.find((x) => x.key === period)?.days || 7;
  // Fill missing days so the chart has a bar per day (MSK dates from backend as YYYY-MM-DD)
  const chart = (() => {
    const map = new Map(timeline.map((r) => [r.bucket, r]));
    const out: { d: string; total: number; resolved: number }[] = [];
    const n = Math.min(days, 90);
    for (let i = n - 1; i >= 0; i--) {
      const dt = new Date(Date.now() + 3 * 3600 * 1000 - i * 86400 * 1000);
      const key = dt.toISOString().slice(0, 10);
      const r: any = map.get(key);
      out.push({ d: key, total: r?.totalTickets || 0, resolved: r?.resolvedTickets || 0 });
    }
    return out;
  })();
  const maxBar = Math.max(1, ...chart.map((c) => c.total));
  const total = overview?.totalTickets || 0;
  const resolved = overview?.resolvedTickets || 0;
  const resolvedPct = total ? Math.round((resolved / total) * 100) : 0;
  const compliance = sla?.resolution?.complianceRate;
  const topOps = [...operators].sort((a, b) => (b.resolvedTickets || 0) - (a.resolvedTickets || 0)).slice(0, 6);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Панель</h1>
            <LiveIndicator pulse={pulse} lastSyncedAt={lastSyncedAt} />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {user?.firstName ? `${user.firstName}, ` : ''}что происходит сейчас и как идёт период
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg bg-muted p-1">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === p.key ? 'bg-card shadow-sm text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <Link to="/tickets/new" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
            <PlusIcon className="h-4 w-4" /> Заявка
          </Link>
          <Link to="/stats" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border bg-card text-sm text-foreground hover:bg-muted/60">
            <ChartBarIcon className="h-4 w-4" /> Статистика
          </Link>
        </div>
      </div>

      {/* Сейчас */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Сейчас{!isAdmin && ' · ваши заявки и новые вашей линии'}</h2>
        <div className="grid gap-4 grid-cols-3">
          <Tile label="Новых без оператора" value={now.newT} icon={InboxStackIcon} tone="amber" to="/tickets?status=new" hint={now.newT ? 'нужно взять' : 'всё разобрано'} />
          <Tile label="В работе" value={now.inProgress} icon={ClockIcon} tone="blue" to="/tickets?status=in_progress" />
          <Tile label="На паузе" value={now.paused} icon={PauseCircleIcon} tone="gray" to="/tickets?status=paused" />
        </div>
      </section>

      {/* За период */}
      <section className="rounded-xl border border-border bg-card shadow-sm p-5 space-y-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="text-base font-semibold text-foreground leading-tight">За {PERIODS.find((p) => p.key === period)?.label}</div>
            <div className="text-sm text-muted-foreground">заявки по дням: всего и решённые</div>
          </div>
          {loading && <span className="text-xs text-muted-foreground">обновляю…</span>}
        </div>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          <Stat label="Заявок" value={total} />
          <Stat label="Решено" value={resolved} sub={`${resolvedPct}% от всех`} />
          <Stat label="Первый ответ" value={fmtMin(overview?.avgFirstResponseTime)} sub="в среднем" />
          <Stat label="Решение" value={fmtMin(overview?.avgResolutionTime)} sub="в среднем" />
          <Stat label="SLA соблюдён" value={compliance == null ? '—' : `${Math.round(compliance)}%`}
            sub={sla?.resolution?.totalResolved ? `из ${sla.resolution.totalResolved} решённых` : undefined} />
        </div>
        <div className="relative">
          {/* Tooltip for the hovered day — rendered above the chart, centred over the bar */}
          {hoverDay !== null && chart[hoverDay] && (
            <div className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full rounded-md bg-foreground text-background text-xs px-2.5 py-1.5 shadow-lg whitespace-nowrap"
              style={{ left: `${((hoverDay + 0.5) / chart.length) * 100}%` }}>
              <div className="font-medium">{ddmm(chart[hoverDay].d)}</div>
              <div>{chart[hoverDay].total} заявок · {chart[hoverDay].resolved} решено</div>
            </div>
          )}
          <div className="flex items-end gap-[3px] h-28" onMouseLeave={() => setHoverDay(null)}>
            {chart.map((c, i) => (
              <div key={c.d} onMouseEnter={() => setHoverDay(i)}
                className={`flex-1 min-w-0 flex flex-col justify-end h-full rounded-t cursor-default ${hoverDay === i ? 'bg-muted' : ''}`}>
                <div className={`w-full rounded-t relative ${hoverDay === i ? 'bg-primary/40' : 'bg-primary/25'}`} style={{ height: `${Math.max(2, (c.total / maxBar) * 100)}%` }}>
                  <div className="absolute bottom-0 left-0 right-0 rounded-t bg-primary" style={{ height: `${c.total ? (c.resolved / c.total) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
            <span>{chart[0] ? ddmm(chart[0].d) : ''}</span>
            <span className="inline-flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-primary/25 inline-block" /> всего</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-primary inline-block" /> решено</span>
            </span>
            <span>{chart.length ? ddmm(chart[chart.length - 1].d) : ''}</span>
          </div>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2 items-start">
        {/* Новые заявки */}
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between p-5 pb-3">
            <div>
              <div className="text-base font-semibold text-foreground leading-tight">Ждут оператора</div>
              <div className="text-sm text-muted-foreground">самые старые сверху</div>
            </div>
            <Link to="/tickets?status=new" className="text-sm text-primary hover:underline">все {now.newT} ›</Link>
          </div>
          {now.newList.length === 0 ? (
            <div className="px-5 pb-5 text-sm text-muted-foreground">Новых заявок нет — все разобраны.</div>
          ) : (
            <div className="divide-y divide-border/70">
              {now.newList.map((t) => (
                <Link key={t.id} to={ticketHref(t.id)} className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/50">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground truncate">{clubName(t)}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.description || t.subject}</div>
                  </div>
                  <span className={`text-xs tabular-nums shrink-0 ${(Date.now() - new Date(t.createdAt).getTime()) > 30 * 60000 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`}>{ago(t.createdAt)}</span>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Операторы */}
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between p-5 pb-3">
            <div>
              <div className="text-base font-semibold text-foreground leading-tight">Операторы за период</div>
              <div className="text-sm text-muted-foreground">по числу решённых</div>
            </div>
            <Link to="/leaderboard" className="text-sm text-primary hover:underline">лидерборд ›</Link>
          </div>
          {topOps.length === 0 ? (
            <div className="px-5 pb-5 text-sm text-muted-foreground">Нет данных за период.</div>
          ) : (
            <div className="px-5 pb-4">
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 text-[11px] text-muted-foreground pb-1 border-b border-border">
                <span>Оператор</span><span className="text-right">заявок</span><span className="text-right">решено</span><span className="text-right">ответ</span>
              </div>
              {topOps.map((o) => (
                <div key={o.operator.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 items-center py-2 text-sm border-b border-border/60 last:border-0">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${o.operator.isOnline ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                    <span className="truncate text-foreground">{o.operator.firstName} {o.operator.lastName}</span>
                  </span>
                  <span className="tabular-nums text-right text-muted-foreground">{o.totalTickets}</span>
                  <span className="tabular-nums text-right font-medium text-foreground">{o.resolvedTickets}</span>
                  <span className="tabular-nums text-right text-muted-foreground">{fmtMin(o.avgFirstResponseTime)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <div className="text-xs text-muted-foreground"><TicketIcon className="inline h-3.5 w-3.5 mr-1 align-[-2px]" />Плитки «Сейчас» кликабельны и ведут в список заявок с нужным фильтром.</div>
    </div>
  );
};

export default DashboardPage;
